import { Box, TableCell, TableRow, Tooltip, Typography } from '@mui/material';
import { ReactNode } from '@tanstack/react-router';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import ErrorIndicator from 'src/components/atoms/error-indicator';
import { Status } from 'src/components/atoms/status';
import { SpaceUnavailabilityState } from '../../api/types/response/space.response';
import { spaceStateColors } from '../../constants/space-state/space-state-colors';
import { spaceStateTranslations } from '../../constants/space-state/space-state-translations';
import { spaceTypesTranslations } from '../../constants/space-types-translations';
import { SpaceType } from '../../types/space-type';
import { formatSpaceBlockingDateRange } from '../../utils/format-space-blocking-date-range';
import { getSpaceCurrentState } from '../../utils/get-space-current-state';
import { PERMANENT_BLOCK_UNTIL_TIMESTAMP } from 'src/constants/spaces';

export interface SpacesActionsSlotPayload {
  token: string;
  name: string;
  spaceBlockedStates: SpaceUnavailabilityState[];
  spacePermanentlyBlockedState: SpaceUnavailabilityState | undefined;
  spaceGroupToken: string | null;
}

interface Props {
  token: string;
  name: string;
  type: SpaceType;
  capacity: number;
  floor: number;
  spaceGroupToken: string | null;
  groupName?: string;
  spaceUnavailabilityStates: SpaceUnavailabilityState[];
  actionsSlot: (payload: SpacesActionsSlotPayload) => ReactNode;
}

export const SpacesTableRow = ({
  token,
  name,
  type,
  capacity,
  floor,
  spaceGroupToken,
  groupName,
  spaceUnavailabilityStates,
  actionsSlot,
}: Props) => {
  const { t } = useTranslation(['spaces', 'common']);

  const spaceBlockedStates = spaceUnavailabilityStates.filter(
    item =>
      (item.state === 'BLOCKED' &&
        ((Date.now() >= item.from && Date.now() <= item.until) ||
          Date.now() < item.from)) ||
      (item.state === 'BLOCKED' && item.isPermanent),
  );

  const spacePermanentlyBlockedState = spaceUnavailabilityStates.find(
    item =>
      item.state === 'BLOCKED' &&
      item.until === PERMANENT_BLOCK_UNTIL_TIMESTAMP,
  );

  const renderFormattedBlockedDates = useMemo(() => {
    return spaceBlockedStates.map(item => (
      <Typography key={`${item.from}-${item.until}`} variant="body2">
        {formatSpaceBlockingDateRange(item.from, item.until)}
      </Typography>
    ));
  }, [spaceBlockedStates]);

  const renderBlockedCell = useMemo(() => {
    switch (spaceBlockedStates.length > 0) {
      case true:
        return (
          <Tooltip
            title={
              spacePermanentlyBlockedState
                ? t('spaces:common.permanentBlock')
                : renderFormattedBlockedDates
            }>
            <span>
              <Status
                color={spaceStateColors['BLOCKED']}
                label={t('common:yes')}
                underlined
              />
            </span>
          </Tooltip>
        );

      case false:
        return (
          <Status
            color={spaceStateColors['AVAILABLE']}
            label={t('common:no')}
          />
        );
    }
  }, [
    spaceBlockedStates.length,
    spacePermanentlyBlockedState,
    t,
    renderFormattedBlockedDates,
  ]);

  return (
    <TableRow>
      <TableCell>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <ErrorIndicator
            tooltipTitle={t('spaces:common.noGroupTooltip')}
            visible={!groupName}
            color="warning.secondary"
          />

          <Typography variant="inherit">{name}</Typography>
        </Box>
      </TableCell>

      <TableCell>
        <Typography variant="body2">
          {t(spaceTypesTranslations[type])}
        </Typography>
      </TableCell>

      <TableCell>
        <Typography variant="body2">{capacity}</Typography>
      </TableCell>

      <TableCell>
        <Typography variant="body2">{floor}</Typography>
      </TableCell>

      <TableCell>
        {groupName ? (
          <Typography variant="body2">{groupName}</Typography>
        ) : (
          <Typography variant="body2" color="textSecondary">
            {t('spaces:common.notAssigned')}
          </Typography>
        )}
      </TableCell>

      <TableCell>
        <Status
          color={
            spaceStateColors[getSpaceCurrentState(spaceUnavailabilityStates)]
          }
          label={t(
            spaceStateTranslations[
              getSpaceCurrentState(spaceUnavailabilityStates)
            ],
          )}
        />
      </TableCell>

      <TableCell>{renderBlockedCell}</TableCell>

      <TableCell align="right" sx={{ cursor: 'default' }}>
        {actionsSlot({
          token,
          name,
          spaceBlockedStates,
          spacePermanentlyBlockedState,
          spaceGroupToken,
        })}
      </TableCell>
    </TableRow>
  );
};
